import { useState } from 'react';

export default function ContactForm({
  title = "Send os en besked",
  subtitle = "Har du spørgsmål til en behandling eller ønsker du at høre mere? Udfyld formularen, så vender vi hurtigst muligt tilbage.",
  watermark = false,
  compact = false,
}) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '', // mandatory honeypot
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState('');

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Venligst indtast dit navn';
    if (!formData.phone.trim()) {
      errs.phone = 'Venligst indtast dit telefonnummer';
    } else if (!/^[0-9+ \-]{6,20}$/.test(formData.phone.trim())) {
      errs.phone = 'Venligst indtast et gyldigt telefonnummer';
    }
    if (!formData.email.trim()) {
      errs.email = 'Venligst indtast din e-mailadresse';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errs.email = 'Venligst indtast en gyldig e-mailadresse';
    }
    if (!formData.message.trim()) errs.message = 'Venligst skriv din besked eller forespørgsel';

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    if (!validate()) return;

    setLoading(true);

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({
          name: '',
          phone: '',
          email: '',
          message: '',
          website_url: '',
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        setServerError(errorData.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch {
      setServerError('Kunne ikke få forbindelse til serveren. Prøv igen senere eller ring til os direkte.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`relative bg-white p-6 sm:p-8 border border-[#E8E1D5] shadow-xs overflow-hidden ${compact ? 'py-6 sm:py-7' : ''}`}>
      {/* Subtle Watermark in background if enabled */}
      {watermark && (
        <div className="pointer-events-none absolute -bottom-6 -right-6 select-none opacity-[0.04] text-[#1E3A5F]">
          <span className="font-serif font-black text-8xl tracking-tighter leading-none">
            DM
          </span>
        </div>
      )}

      <div className="relative z-10 mb-5">
        <h3 className="text-xl sm:text-2xl font-bold text-[#1E3A5F] font-serif">{title}</h3>
        {subtitle && <p className="text-slate-600 text-xs sm:text-sm mt-1 leading-relaxed">{subtitle}</p>}
      </div>

      {submitted ? (
        <div className="relative z-10 p-5 bg-[#F3F4F6] border border-[#1E3A5F] text-[#1E3A5F]">
          <div className="flex items-center gap-2 mb-1.5">
            <i className="fa-solid fa-circle-check text-emerald-600 text-lg" aria-hidden="true" />
            <h4 className="text-base font-bold">Mange tak for din besked!</h4>
          </div>
          <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
            Vi har modtaget din henvendelse og vender hurtigst muligt tilbage. Du er også velkommen til at ringe på{' '}
            <a href="tel:28583907" className="font-bold underline whitespace-nowrap">
              28 58 39 07
            </a>.
          </p>
          <button
            type="button"
            onClick={() => setSubmitted(false)}
            className="mt-4 px-3.5 py-1.5 bg-[#1E3A5F] text-white text-xs font-semibold hover:shadow-xs transition-shadow"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate className="relative z-10 space-y-4">
          {/* Honeypot field (hidden from humans) */}
          <div className="sr-only absolute" aria-hidden="true">
            <label htmlFor="website_url">Hjemmeside url (lad feltet stå tomt)</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              tabIndex={-1}
              autoComplete="off"
              value={formData.website_url}
              onChange={handleChange}
            />
          </div>

          {serverError && (
            <div className="p-3 bg-red-50 border-b border-red-300 text-red-700 text-xs flex items-center gap-2">
              <i className="fa-solid fa-triangle-exclamation shrink-0" aria-hidden="true" />
              <span>{serverError}</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Navn */}
            <div>
              <label htmlFor="name" className="block text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1">
                Navn <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Dit fulde navn"
                className={`w-full px-0 py-2 border-0 border-b bg-transparent text-sm transition-colors outline-hidden focus:border-[#1E3A5F] focus:ring-0 ${
                  errors.name ? 'border-red-500' : 'border-[#D8CDBE]'
                }`}
                required
              />
              {errors.name && <p className="text-[11px] text-red-600 mt-1">{errors.name}</p>}
            </div>

            {/* Telefon */}
            <div>
              <label htmlFor="phone" className="block text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1">
                Telefonnummer <span className="text-red-600">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="28 58 39 07"
                className={`w-full px-0 py-2 border-0 border-b bg-transparent text-sm transition-colors outline-hidden focus:border-[#1E3A5F] focus:ring-0 ${
                  errors.phone ? 'border-red-500' : 'border-[#D8CDBE]'
                }`}
                required
              />
              {errors.phone && <p className="text-[11px] text-red-600 mt-1">{errors.phone}</p>}
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1">
              E-mailadresse <span className="text-red-600">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="din@adresse.dk"
              className={`w-full px-0 py-2 border-0 border-b bg-transparent text-sm transition-colors outline-hidden focus:border-[#1E3A5F] focus:ring-0 ${
                errors.email ? 'border-red-500' : 'border-[#D8CDBE]'
              }`}
              required
            />
            {errors.email && <p className="text-[11px] text-red-600 mt-1">{errors.email}</p>}
          </div>

          {/* Besked */}
          <div>
            <label htmlFor="message" className="block text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1">
              Beskrivelse <span className="text-red-600">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              rows={compact ? 2 : 3}
              value={formData.message}
              onChange={handleChange}
              placeholder="Skriv din besked eller spørgsmål..."
              className={`w-full px-0 py-2 border-0 border-b bg-transparent text-sm transition-colors outline-hidden focus:border-[#1E3A5F] focus:ring-0 resize-y ${
                errors.message ? 'border-red-500' : 'border-[#D8CDBE]'
              }`}
              required
            />
            {errors.message && <p className="text-[11px] text-red-600 mt-1">{errors.message}</p>}
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3 bg-[#1E3A5F] text-white text-xs sm:text-sm font-semibold rounded-full hover:shadow-md transition-shadow disabled:opacity-50"
            >
              {loading ? (
                <>
                  <i className="fa-solid fa-circle-notch fa-spin text-xs" aria-hidden="true" />
                  <span>Sender...</span>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-paper-plane text-[#C9A770]" aria-hidden="true" />
                  <span>Indsend formular</span>
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
